import { PropsWithChildren } from 'react';

import { translation, TranslatedComponent } from '@hh.ru/front-static-app';
import Column from 'bloko/blocks/column';
import { H1 } from 'bloko/blocks/header';
import Stepwise, { StepwiseStep } from 'bloko/blocks/stepwise';
import Text from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';

import PageLayout from 'src/app/layouts/PageLayout';
import { useIsBelarus } from 'src/hooks/useCountries';
import { ApplicantService } from 'src/types/applicantServices';

type TrlType = {
    [key in ApplicantService]: {
        title: string;
        description: string;
        status?: { [key: number]: string };
        warningBY: string;
    };
};

const TrlKeys: TrlType = {
    [ApplicantService.ResumeAudit]: {
        title: 'applicantServices.order.resumeAudit.title',
        description: 'applicantServices.order.resumeAudit.description',
        status: {
            0: 'applicantServices.order.resumeAudit.status.0',
            1: 'applicantServices.order.resumeAudit.status.1',
            2: 'applicantServices.order.resumeAudit.status.2',
            3: 'applicantServices.order.resumeAudit.status.3',
        },
        warningBY: 'applicantServices.order.warning.text',
    },
    [ApplicantService.CompleteResume]: {
        title: 'applicantServices.order.completeResumeBase.title',
        description: '',
        status: {
            0: 'applicantServices.order.completeResume.status.0',
            1: 'applicantServices.order.completeResume.status.1',
            2: 'applicantServices.order.completeResume.status.2',
            3: 'applicantServices.order.completeResume.status.3',
            4: 'applicantServices.order.completeResume.status.4',
            5: 'applicantServices.order.completeResume.status.5',
        },
        warningBY: 'applicantServices.order.warning.text',
    },
    [ApplicantService.CompleteResumeBase]: {
        title: 'applicantServices.order.completeResumeBase.title',
        description: '',
        status: {
            0: 'applicantServices.order.completeResume.status.0',
            1: 'applicantServices.order.completeResume.status.1',
            2: 'applicantServices.order.completeResume.status.2',
            3: 'applicantServices.order.completeResume.status.3',
            4: 'applicantServices.order.completeResume.status.4',
            5: 'applicantServices.order.completeResume.status.5',
        },
        warningBY: 'applicantServices.order.warning.text',
    },
    [ApplicantService.CompleteResumeOptimal]: {
        title: 'applicantServices.order.completeResumeOptimal.title',
        description: '',
        status: {
            0: 'applicantServices.order.completeResume.status.0',
            1: 'applicantServices.order.completeResume.status.1',
            2: 'applicantServices.order.completeResume.status.2',
            3: 'applicantServices.order.completeResume.status.3',
            4: 'applicantServices.order.completeResume.status.4',
            5: 'applicantServices.order.completeResume.status.5',
        },
        warningBY: 'applicantServices.order.warning.text',
    },
    [ApplicantService.CompleteResumeMaximal]: {
        title: 'applicantServices.order.completeResumeMaximal.title',
        description: '',
        status: {
            0: 'applicantServices.order.completeResume.status.0',
            1: 'applicantServices.order.completeResume.status.1',
            2: 'applicantServices.order.completeResume.status.2',
            3: 'applicantServices.order.completeResume.status.3',
            4: 'applicantServices.order.completeResume.status.4',
            5: 'applicantServices.order.completeResume.status.5',
        },
        warningBY: 'applicantServices.order.warning.text',
    },
    [ApplicantService.CompleteResumeConsultOnly]: {
        title: 'applicantServices.order.completeResumeConsultOnly.title',
        description: '',
        status: {
            0: 'applicantServices.order.completeResumeConsultOnly.status.0',
            1: 'applicantServices.order.completeResumeConsultOnly.status.1',
            2: 'applicantServices.order.completeResumeConsultOnly.status.2',
            4: 'applicantServices.order.completeResumeConsultOnly.status.3',
        },
        warningBY: '',
    },
    [ApplicantService.CompleteResumeInterviewPractice]: {
        title: 'applicantServices.order.completeResumeInterviewPractice.title',
        description: '',
        status: {
            0: 'applicantServices.order.completeResumeInterviewPractice.status.0',
            1: 'applicantServices.order.completeResumeInterviewPractice.status.1',
            2: 'applicantServices.order.completeResumeInterviewPractice.status.2',
            4: 'applicantServices.order.completeResumeInterviewPractice.status.3',
        },
        warningBY: '',
    },
    [ApplicantService.CareerGuidance]: {
        title: 'applicantServices.career_guidance.title.new',
        description: 'proforientation.payment.instruction.new',
        warningBY: '',
    },
    [ApplicantService.ResumeRenewal]: {
        title: '',
        description: '',
        warningBY: '',
    },
    [ApplicantService.TargetAgencies]: {
        title: '',
        description: '',
        warningBY: '',
    },
    [ApplicantService.ResumeTargetEmployer]: {
        title: '',
        description: '',
        warningBY: '',
    },
    [ApplicantService.VacancyResponsesSummary]: {
        title: '',
        description: '',
        warningBY: '',
    },
    [ApplicantService.CareerMarathon]: {
        title: '',
        description: '',
        warningBY: '',
    },
};

const SERVICE_STATUS_MAP = {
    [ApplicantService.CompleteResume]: [0, 1, 2, 3, 4, 5],
    [ApplicantService.CompleteResumeBase]: [0, 1, 2, 3, 4, 5],
    [ApplicantService.CompleteResumeOptimal]: [0, 1, 2, 3, 4, 5],
    [ApplicantService.CompleteResumeMaximal]: [0, 1, 2, 3, 4, 5],
    [ApplicantService.CompleteResumeConsultOnly]: [0, 1, 2, 4],
    [ApplicantService.CompleteResumeInterviewPractice]: [0, 1, 2, 4],
    [ApplicantService.CareerGuidance]: null,
    [ApplicantService.ResumeAudit]: [0, 1, 2, 3],
    [ApplicantService.ResumeRenewal]: null,
    [ApplicantService.TargetAgencies]: null,
    [ApplicantService.ResumeTargetEmployer]: null,
    [ApplicantService.VacancyResponsesSummary]: null,
    [ApplicantService.CareerMarathon]: null,
} as const;

interface ApplicantServiceLayoutProps {
    service: ApplicantService;
    status?: number;
    showDescription?: boolean;
    serviceTitle?: string;
    serviceDescription?: string;
}

const ApplicantServiceLayout: TranslatedComponent<ApplicantServiceLayoutProps> = ({
    children,
    service,
    status,
    showDescription,
    serviceTitle,
    serviceDescription,
    trls,
}) => {
    const isBelarus = useIsBelarus();
    const stepWise = SERVICE_STATUS_MAP[service];
    const description =
        serviceDescription || (TrlKeys[service].description.length ? trls[TrlKeys[service].description] : null);
    const title = serviceTitle || trls[TrlKeys[service].title];
    const warningBY = TrlKeys[service].warningBY.length ? trls[TrlKeys[service].warningBY] : null;

    return (
        <PageLayout title={title}>
            <Column xs="4" s="8" m="12" l="16">
                <H1>{title}</H1>
                {showDescription && description && (
                    <>
                        <VSpacing base={4} />
                        <Text>{description}</Text>
                    </>
                )}
                {isBelarus && warningBY && (
                    <>
                        <VSpacing base={4} />
                        <Text>{warningBY}</Text>
                    </>
                )}
                {!!stepWise && (
                    <>
                        <VSpacing base={5} />
                        <Stepwise>
                            {stepWise.map((key) => {
                                const trlKey = TrlKeys[service].status?.[key];
                                if (!trlKey) {
                                    return <></>;
                                }
                                return (
                                    <StepwiseStep
                                        data-qa={`applicant-service-step ${
                                            status === key ? 'applicant-service-step-current' : ''
                                        }`}
                                        key={key}
                                        title={trls[trlKey]}
                                        current={status === key}
                                    />
                                );
                            })}
                        </Stepwise>
                    </>
                )}
                <VSpacing base={8} />
                {children}
            </Column>
        </PageLayout>
    );
};

export default translation<PropsWithChildren<ApplicantServiceLayoutProps>>(ApplicantServiceLayout);
